import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function MirrorEquationCalculator() {
  const [objectDistance, setObjectDistance] = useState("");
  const [imageDistance, setImageDistance] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Mirror Equation Calculator", url: "https://calclab.app/calculator/mirror-equation" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Mirror Equation Calculator",
      "Calculate focal length using the mirror equation (1/f = 1/do + 1/di). Free online optics calculator for curved mirrors.",
      "https://calclab.app/calculator/mirror-equation",
      lang
    )
  ]);

  const calculate = () => {
    const do_val = parseFloat(objectDistance);
    const di = parseFloat(imageDistance);
    if (isNaN(do_val) || isNaN(di)) {
      toast.error("Enter valid values");
      return;
    }
    const f = 1 / (1/do_val + 1/di);
    setResult(f);
    toast.success("Complete!");
  };

  return (
    <PageLayout calculatorName="Mirror Equation Calculator" calculatorPath="/calculator/mirror-equation">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader><CardTitle>1/f = 1/d_o + 1/d_i</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Object Distance (cm)</Label><Input type="number" value={objectDistance} onChange={(e) => setObjectDistance(e.target.value)} /></div>
            <div><Label>Image Distance (cm)</Label><Input type="number" value={imageDistance} onChange={(e) => setImageDistance(e.target.value)} /></div>
            <Button onClick={calculate} className="w-full"><Calculator className="mr-2 h-4 w-4" />Calculate</Button>
            {result !== null && (
              <>
                <Card className="bg-primary/10 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-2xl font-bold break-all overflow-wrap-anywhere">{result.toFixed(2)} cm</p>
                      <p className="text-sm">Focal Length</p>
                    </div>
                    <CopyResultButton value={`${result.toFixed(2)} cm`} />
                  </div>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["mirror-equation"]} trigger={result} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
